import { length3, normalize3, type Vec3 } from './vector'

/** Rotation quaternion in [x, y, z, w] order. */
export type Quat = readonly [number, number, number, number]

export function normalizeQuaternion(value: Quat): Quat {
  const length = Math.hypot(...value)
  if (!(length > 0) || !Number.isFinite(length)) throw new RangeError('Quaternion must have finite, nonzero length')
  return [value[0] / length, value[1] / length, value[2] / length, value[3] / length]
}

export function quaternionFromAxisAngle(axis: Vec3, angle: number): Quat {
  if (!(length3(axis) > 0) || !axis.every(Number.isFinite) || !Number.isFinite(angle)) throw new RangeError('Rotation requires a finite angle and nonzero axis')
  const direction = normalize3(axis)
  const sine = Math.sin(angle / 2)
  return [direction[0] * sine, direction[1] * sine, direction[2] * sine, Math.cos(angle / 2)]
}

/** Applies b, then a, matching matrix multiplication. */
export function multiplyQuaternion(a: Quat, b: Quat): Quat {
  const [ax, ay, az, aw] = a
  const [bx, by, bz, bw] = b
  return normalizeQuaternion([
    aw * bx + ax * bw + ay * bz - az * by,
    aw * by - ax * bz + ay * bw + az * bx,
    aw * bz + ax * by - ay * bx + az * bw,
    aw * bw - ax * bx - ay * by - az * bz,
  ])
}

/** Shortest-path rotation interpolation, including equivalent antipodal quaternions. */
export function slerpQuaternion(start: Quat, end: Quat, progress: number): Quat {
  const a = normalizeQuaternion(start)
  let b = normalizeQuaternion(end)
  const t = Math.max(0, Math.min(1, progress))
  let dot = a.reduce((sum, value, index) => sum + value * b[index]!, 0)
  if (dot < 0) {
    b = [-b[0], -b[1], -b[2], -b[3]]
    dot = -dot
  }
  if (dot > 0.9995) return normalizeQuaternion([
    a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t,
    a[2] + (b[2] - a[2]) * t, a[3] + (b[3] - a[3]) * t,
  ])
  const theta = Math.acos(Math.min(1, dot))
  const divisor = Math.sin(theta)
  const from = Math.sin((1 - t) * theta) / divisor
  const to = Math.sin(t * theta) / divisor
  return normalizeQuaternion([
    a[0] * from + b[0] * to, a[1] * from + b[1] * to,
    a[2] * from + b[2] * to, a[3] * from + b[3] * to,
  ])
}

/** Converts an orthonormal, right-handed basis whose columns are local X, Y and Z. */
export function quaternionFromBasis(right: Vec3, up: Vec3, back: Vec3): Quat {
  const m00 = right[0], m01 = up[0], m02 = back[0]
  const m10 = right[1], m11 = up[1], m12 = back[1]
  const m20 = right[2], m21 = up[2], m22 = back[2]
  const trace = m00 + m11 + m22
  if (trace > 0) {
    const s = Math.sqrt(trace + 1) * 2
    return normalizeQuaternion([(m21 - m12) / s, (m02 - m20) / s, (m10 - m01) / s, s / 4])
  }
  if (m00 > m11 && m00 > m22) {
    const s = Math.sqrt(1 + m00 - m11 - m22) * 2
    return normalizeQuaternion([s / 4, (m01 + m10) / s, (m02 + m20) / s, (m21 - m12) / s])
  }
  if (m11 > m22) {
    const s = Math.sqrt(1 + m11 - m00 - m22) * 2
    return normalizeQuaternion([(m01 + m10) / s, s / 4, (m12 + m21) / s, (m02 - m20) / s])
  }
  const s = Math.sqrt(1 + m22 - m00 - m11) * 2
  return normalizeQuaternion([(m02 + m20) / s, (m12 + m21) / s, s / 4, (m10 - m01) / s])
}
